// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the librarian
//  ---------------------------------------------------------------------------
//  The Main DB: one database, shipped with the app, whose rows are every record
//  on the account. It is not a database somebody made. It is ManDana's own
//  description of what the user has, materialised on read the way the built-in
//  Prayers project already is — the same pattern, one shelf up.
//
//  NOTHING IS SEEDED. No edge is written when a record is created, nothing is
//  copied in, nothing has to be kept in step. A catalogue that must be filled
//  in before it is true is a catalogue that is wrong the moment you look away.
//  Every record is here because it EXISTS: the rows are recomputed from the
//  record index on every read, so a record written a second ago is in the
//  catalogue, and one deleted a second ago is out of it, with no bookkeeping in
//  between. That is also why the Main DB stores no rows of its own — there is
//  nothing to add here, because a row is a record kept somewhere else.
//
//  HEADLESS, AND THAT IS THE POINT. This module knows nothing about the DOM,
//  React, or any store the app happens to hold in state. The table is one
//  consumer of `mdLibrarianRows`; the AI Organizer will be another, reading the
//  identical rows through the identical function. An agent that scrapes a
//  rendered view is an agent that breaks on the next CSS change, so the seam is
//  drawn here, in the phase before the one that needs it.
//
//  A ROW'S ID IS ITS REF KEY. An ordinary member row is named by its edge
//  document (see md-membership.ts); these rows have no edge, so they carry the
//  ref key itself — colons and all. Nothing on the compose-and-render path
//  validates a row id against the `[a-z0-9]{6,40}` grammar, which is what makes
//  that legal; md-database.ts's cell-write door is the one place that cared,
//  and it now reads a ref key as readily as an edge id.
//
//  AND AN EDGE IS EARNED, NOT GIVEN. The first time the user fills a cell the
//  user added, md-database.ts creates the membership edge for that one record.
//  So the per-render edge scan stays proportional to the records somebody has
//  EDITED rather than to every record they own — six thousand records still
//  cost zero edges — and the row keeps its ref-key id when it happens, because
//  a row that changes identity under the cursor is a row that loses the cursor.
//
//  TWO KINDS OF CELL, AND THE LINE IS LOAD-BEARING. Record-owned cells (the
//  title, the type, the project) belong to the record's own home document: the
//  Main DB is the interface, not the owner. Database-owned cells are any
//  property the user adds here, and those live on the edge. Blur the line and
//  life-structure starts accumulating inside edges, which is the
//  one-upward-pointer law dying quietly. `mdLibrarianOwns` is where the line is
//  written down, and `mdLibrarianCellSet` is the one door through it.
//
//  PARENT IS WALKED, NEVER STORED. `pid` → project → `pursuitId` → pursuit →
//  `domainId` → domain, read out broad to narrow because that is the order a
//  person says an address in. A generic `parent` field on a record stays
//  forbidden: md-record.ts's law is that TYPE says what a record is and `pid`
//  says what it belongs to, and a second pointer is a second opinion waiting to
//  disagree with the first. Backlinks set the precedent — derived on every read
//  for exactly this reason (md-wikilink.ts).
//
//  A MISSING RUNG IS A BLANK, NOT A THROW. Trash moves a container and its
//  containment subtree together, but sync, an older build or an independently
//  missing parent can still leave a short chain. That is a real state and not a
//  reason to take down the table, so the chain renders what it still has.
//
//  ── the X-ray ──────────────────────────────────────────────────────────────
//
//  THREE COLUMNS ANSWER THE QUESTION EVERY LOCAL-FIRST APP LEAVES OPEN. Where
//  the record is kept, when it was last written, and whether the server has that
//  version. All three are derived on every read from what the app already holds;
//  the X-ray stores nothing, and being in OWNED is what stops the table from
//  minting an edge the first time somebody clicks into one of them.
//
//  THE SYNC STATE IS md-sync's PUSH RULE, READ BACKWARDS. `md:syncMeta` holds,
//  for every document this device has sent, the clock and the content hash it
//  sent; the push loop's whole decision is "is this document's hash the one I
//  recorded". mdSyncStates answers that same question about that same table, so
//  the column cannot drift from the layer it reports on. Four readings:
//
//    Built in            — no document at all. Nothing to sync, nothing to lose.
//    This device only    — no row in the table, or a recorded deletion.
//    Changed since sync  — a row, but the content has moved since.
//    Synced              — a row, and the hash still matches.
//
//  HELD AND CONFLICTED WERE CONSIDERED AND DROPPED, and the reason is in the
//  layer rather than in this file. No per-document conflict state is retained
//  anywhere — resolution is last-writer-wins on `clientSeenAt` — so a Conflicted
//  column would be a guess wearing a label. And the only held keys are
//  `record-index`, which is not a record, and an oversized board DRAWING, which
//  is never a record's home: a board record is kept in the `md:boards` index,
//  and that index is always pushed. A held record cannot presently exist, and
//  inventing a state nothing can enter is worse than three honest ones.
//
//  A BUILT-IN CAN STILL HAVE A DOCUMENT. A domain's identity is code, so the
//  record index files it under no home; its WRITING is an ordinary document and
//  that document syncs like anyone else's. So a record is asked about every
//  document it has and reported at the WORST of them, and "Built in" is reserved
//  for a record with nothing stored at all. The identity is the app's and the
//  writing is the user's, and it is the user's half that can be lost — reporting
//  "Built in" over unsynced writing is the one lie this column must never tell.
//
//  A MANIFESTATION READS "This device only" FOREVER, and that is the column
//  doing its job rather than failing at it. md-manifest.ts keeps the library off
//  the wire on purpose while the bytes are device-local. The honest reading of a
//  document no server has is that no server has it.
//
//  ── the findings ───────────────────────────────────────────────────────────
//
//  A CATALOGUE OF WHAT YOU HAVE IS A FILING CABINET. The value this phase was
//  approved for is the SHAPE of what somebody is carrying, and half of that
//  shape is the parts of life they have written nothing in. So the Main DB opens
//  grouped by domain and every row carries, in a column of its own, what is
//  missing about it.
//
//  ALL TEN DOMAINS ARE ALWAYS ON SCREEN, AND NOT ONE LINE OF THE TABLE CHANGED
//  TO MAKE IT SO. DbTable drops a group holding no rows (md-database.jsx:622),
//  which is right for every user database and is not ours to touch. It never
//  bites here because a domain IS a record and its own row groups under itself
//  (md-resolve.ts:225) — the domain nothing is filed in still renders, holding
//  one row, and that row's Findings cell is the sentence worth reading.
//
//  A `select`, NOT TEXT, AND THE OPTION LIST IS THE SORT ORDER. Grouping on text
//  would hand the ten to `localeCompare` and open the Librarian on Career; the
//  option list makes Spirit first because prayer is the axis. The ids carry a
//  `domain` prefix for the reason `rectype` is not `type`: an option id is held
//  to the same `[a-z0-9]{6,40}` floor, and `e6` would be silently replaced.
//
//  AND THERE IS AN ELEVENTH OPTION. A manifestation, a database and a database
//  row resolve to no domain by design, and a domain id the app no longer ships
//  is read as no domain at all. Without a name for that they would fall into
//  md-database.ts's unnamed bucket and be labelled "Empty" by a shared component
//  that has no idea what it is looking at. `domainnone` is that name, it sorts
//  last, and DbTable drops it on the account where nothing is in it.
//
//  "UNTYPED RECORDS" IS DELIVERED AS "NOT IN A DOMAIN". The plan asks for untyped
//  records in the findings set; mdRecordType always answers, so an untyped record
//  cannot exist and a finding for it would never fire. The real gap the plan is
//  pointing at — a record the spine does not place — is unplaced, and that is
//  what ships.
//
//  THE COLUMN AND THE ORGANIZER'S QUEUE ARE ONE ARRAY, sliced two ways out of
//  one composition. Two functions answering "what is rotting" is two answers
//  that disagree by the end of the quarter, and the agent reading the second one
//  would be pruning an account the user cannot see. A caller wanting two slices
//  composes twice; the table wants one.
//
//  NONE OF IT SPEAKS TO THE USER ABOUT THE USER. "Nothing filed here" is a fact
//  about a domain; "you haven't filed anything here" is the app having a view
//  about somebody's life. The law that survives L-0 — ManDana never shames — is
//  aimed exactly here, because shame is the direction a findings list drifts in
//  when nobody is watching it.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  /** Well-known and permanent: nine lowercase letters, so it satisfies the same
      `[a-z0-9]{6,40}` id grammar every user-made database is held to, and no
      generated id (`d` + random) can ever collide with it. */
  const ID = 'librarian';
  const NAME = 'Main DB';

  /** The columns the app ships. `rectype` rather than `type` because a property
      id is held to the same six-character floor as a database id — a shorter id
      would be silently replaced by a generated one at sanitise time. */
  const TITLE = 'title';
  const TYPE = 'rectype';
  const PARENT = 'parent';
  const PROJECT = 'project';
  /* `storage` and `syncstate` for the reason `rectype` is: `home` and `sync`
     are under the six-character floor, and md-database.ts's sanitiser silently
     replaces a short property id with a generated one — the column would still
     render and nothing would ever be able to name it again. */
  const HOME = 'storage';
  const WRITTEN = 'written';
  const SYNC = 'syncstate';
  /* Both clear the floor on their own — `domain` is six characters and
     `findings` is eight — so neither needs the treatment `rectype` needed. */
  const DOMAIN = 'domain';
  const FINDINGS = 'findings';

  /** Every sentence the findings column can say, in one place. Each is a fact
      about a record and none of them contains the word "you": the successor to
      the no-gamification law cuts against shame as hard as against celebration,
      and a findings list is where an app slips into it. `unplaced` doubles as
      the name of the eleventh group, deliberately — the row says the same thing
      the shelf it sits on says. */
  const WORDS = {
    empty: 'Nothing filed here',
    project: 'Not in a project',
    pursuit: 'Not in a pursuit',
    unplaced: 'Not in a domain',
    unlinked: 'No links',
    stale: (day: string): string => `Nothing written since ${day}`,
  };

  /** Cells the Main DB shows but does not own. Written to the record's own home
      or not at all; NEVER to an edge — being in this set is what routes a write
      through mdLibrarianCellSet instead of minting an edge for it. Parent is
      here because it is read-only and would otherwise, being plain text, look
      to md-database.ts exactly like a column the user had added, and the three
      X-ray columns are here for exactly that reason too. */
  const OWNED = new Set<string>([TITLE, TYPE, PARENT, PROJECT, HOME, WRITTEN, SYNC,
    DOMAIN, FINDINGS]);

  /** The ten, in the app's own order, plus a named home for the placeless. Read
      off MD_DOMAINS rather than copied, so a domain added to the app appears
      here without this file being edited. */
  const NO_DOMAIN_OPTION = 'domainnone';
  function domainOptions(): Array<{ id: string; name: string }> {
    const out = (g.MD_DOMAINS || []).map((d: any) => ({ id: DOMAIN + d.id, name: d.label }));
    out.push({ id: NO_DOMAIN_OPTION, name: WORDS.unplaced });
    return out;
  }

  /** The database as shipped. Returned only for the Main DB's own id, and only
      while no document is stored under it: the moment the user changes anything
      — a column, a view, a filter — md-database.ts saves a real document and
      that document is what is read from then on. Deliberately partial; the
      database sanitiser fills in every field a view needs, so this file does
      not carry a copy of the view shape to drift out of date. */
  function mdLibrarianDatabase(dbId?: unknown): Record<string, unknown> | null {
    if (dbId !== undefined && dbId !== ID) return null;
    return {
      version: 3, id: ID, name: NAME,
      properties: [
        { id: TITLE, name: 'Record', type: 'title' },
        { id: TYPE, name: 'Type', type: 'text' },
        /* Both plain text, and Project deliberately NOT a `select`. This
           definition is read only until the user first changes anything here,
           after which a stored document answers forever — so a baked-in option
           list would freeze the projects that happened to exist on the day
           somebody first dragged a column. A text cell that names a project is
           resolved against the real projects at write time instead. */
        { id: PARENT, name: 'Parent', type: 'text' },
        { id: PROJECT, name: 'Project', type: 'text' },
        /* The X-ray, and all three deliberately plain text. `written` is NOT an
           `updated_time`: that type renders as `new Date(row.updated)
           .toISOString()`, so a record the app keeps no clock for — the ten
           domains, a built-in project nobody has written in — would read 1970
           rather than reading blank. Blank is the truth, and a column that
           invents a moment the user was not there for is the whole failure this
           phase exists to avoid. */
        { id: HOME, name: 'Storage', type: 'text' },
        { id: WRITTEN, name: 'Last written', type: 'text' },
        { id: SYNC, name: 'Sync', type: 'text' },
        /* A `select` so the ten group in ManDana's order rather than in the
           alphabet's — and so md-database.ts seeds a group for every one of
           them before a single row is read. It is the only property here that
           is grouped ON and never shown: the group header already says the
           word, and a column repeating it in every row is noise. */
        { id: DOMAIN, name: 'Part of life', type: 'select', options: domainOptions() },
        { id: FINDINGS, name: 'Findings', type: 'text' },
      ],
      rows: [],
      views: [{ id: 'mainview', name: 'All records', type: 'table', groupBy: DOMAIN,
        visiblePropertyIds: [TITLE, TYPE, PARENT, PROJECT, HOME, WRITTEN, SYNC, FINDINGS] }],
      activeViewId: 'mainview',
      locked: false, templates: [], automations: [],
      /* Shipped, not created. A zero says "the app has always had this" rather
         than inventing a moment the user was not there for. */
      created: 0, updated: 0, revision: 1,
    };
  }

  /** Is this cell the record's own rather than this database's? */
  function mdLibrarianOwns(dbId: unknown, propertyId: unknown): boolean {
    return dbId === ID && typeof propertyId === 'string' && OWNED.has(propertyId);
  }

  /** The two structural cells for one row: the whole address above the record,
      and the project rung of it on its own so that rung can be edited.

      A PROJECT'S OWN NAME IS NOT ITS PARENT. A record of type `project` is the
      project rung, so it shows the two rungs above it and leaves the Project
      cell empty — the alternative reads as a project filed inside itself. The
      same holds a shelf up for a pursuit.

      A DATABASE ROW ANSWERS WITH ITS DATABASE, the one record kind whose place
      is not reached through `pid`. It is still the honest answer to "what holds
      this", which is the question the column asks. */
  function structure(resolved: any, dbName: (id: string) => string): { parent: string; project: string } {
    const type = resolved.ref && resolved.ref.type;
    if (type === 'domain') return { parent: '', project: '' };
    if (type === 'dbrow') return { parent: resolved.databaseId ? dbName(resolved.databaseId) : '', project: '' };
    /* A project reads its own row of the spine; everything else reads the
       project it was filed under, which the resolver already handed over. */
    const own = type === 'project';
    const projectId = own ? resolved.ref.id : resolved.projectId;
    const project = (typeof projectId === 'string' && projectId && g.mdProjectById)
      ? g.mdProjectById(projectId) : null;
    const pursuit = (project && project.pursuitId && g.mdPursuitById)
      ? g.mdPursuitById(project.pursuitId) : null;
    const domain = (resolved.domainId && g.mdDomainById) ? g.mdDomainById(resolved.domainId) : null;
    const rungs = [domain ? domain.label : '', pursuit ? pursuit.name : ''];
    if (!own) rungs.push(project ? project.name : '');
    /* filter(Boolean) is the missing-rung rule: a deleted pursuit costs its own
       word and nothing else, and no rung's absence can throw. */
    return { parent: rungs.filter(Boolean).join(' · '), project: (own || !project) ? '' : project.name };
  }

  /* ── the X-ray ───────────────────────────────────────────────────────────── */

  /** md-sync.js's MD_SYNC_STATE_* values, ordered worst first and given the
      words the user reads. Worst first because a record's reading is the worst
      of its documents: one unsynced paragraph is the thing worth knowing, and
      averaging it away against a synced index row would hide exactly the case
      the column exists for. */
  const SYNC_WORDS: Array<[string, string]> = [
    ['local', 'This device only'],
    ['changed', 'Changed since sync'],
    ['synced', 'Synced'],
  ];
  const BUILT_IN = 'Built in';

  /** A record's writing, for the three kinds that keep it in a document of its
      own — asked of the module that owns that storage key rather than spelled
      out here, so the key keeps having one owner. Everything else keeps its
      writing inside its home, which the index already names. */
  function writingOf(resolved: any): { key: string; at: number } | null {
    const type = resolved.ref && resolved.ref.type;
    const ask = type === 'domain' ? g.mdDomainWriting
      : type === 'pursuit' ? g.mdPursuitWriting
        : type === 'project' ? g.mdProjectWriting : null;
    if (!ask) return null;
    const writing = ask(resolved.ref.id);
    /* A clock of zero is how each of the three says "nothing is written there".
       The key is still returned, so presence has to be read off `at`. */
    return writing && writing.key && writing.at > 0 ? writing : null;
  }

  /** `YYYY-MM-DD HH:MM`, in the reader's own timezone, and blank for no clock.

      NOT mdDayKey. That helper lives in md-theme.jsx, a React module this one
      must never need — headless is the contract, and a date formatter is not
      worth breaking it for. */
  function stamp(at: unknown): string {
    if (typeof at !== 'number' || !Number.isFinite(at) || at <= 0) return '';
    const d = new Date(at);
    const pad = (n: number): string => (n < 10 ? '0' : '') + n;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      + ` ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** What the person actually typed, with the title taken back off the front.

      The resolver hands back one searchable string — name, then a newline, then
      the body — because search wants both. A card preview does not: the board
      and the list already print the title in bold, so passing the seed text
      through would print it twice, once as the heading and again as the first
      words of its own snippet. */
  function bodyOf(resolved: any): string {
    const text = typeof resolved.text === 'string' ? resolved.text : '';
    const title = String(resolved.title || '');
    if (!title || !text.startsWith(title)) return text;
    return text.slice(title.length).replace(/^\r?\n/, '');
  }

  /* ── the findings ────────────────────────────────────────────────────────── */

  /** The date alone. A finding is about a stretch of months, and a sentence
      that says "since 2026-01-10 14:07" is answering a question nobody asked. */
  function dayWords(at: unknown): string { return stamp(at).slice(0, 10); }

  /** Two months of silence. Long enough that a slow month is not an accusation,
      short enough that a part of life quietly let go of is named within the
      season it happened in. */
  const STALE_DAYS = 60;

  /** Severity, and the order both the cell and the queue are read in — a person
      reads the first note in the cell, and the Organizer works its list from the
      top. Nothing filed at all outranks nothing filed lately, which outranks a
      single record standing in the wrong place. */
  const KIND_ORDER = ['empty', 'stale', 'orphan', 'unplaced', 'unlinked'];

  /** The record kinds that belong to no part of life BY DESIGN. md-resolve.ts
      hands each of them `domainId: null` deliberately — a manifestation is the
      app's own library, a database and its rows are a structure rather than a
      life — so asking them to be placed would be the catalogue reporting its own
      architecture as somebody's mess. They still group under a named shelf. */
  const SPINE_PLACELESS = new Set(['craft', 'database', 'dbrow']);

  /* The rows. One per record the index names and the resolver can still find —
     the index says what to ask about and mdRecordResolve answers, so a stale
     entry costs one skipped row rather than a wrong one.

     THE HOMES ARE READ ONCE. mdRecordHomes() opens the day maps and the project
     record index; doing it per record would turn a catalogue of six thousand
     into six thousand full reads. md-resolve.ts's header asks every caller that
     resolves MANY refs to build homes once and pass them along, and this is the
     largest such caller in the app.

     AND THE INDEX IS READ ONCE, FOR BOTH HALVES OF WHAT IT KNOWS. It names the
     records AND names the document each one is kept in, which is the Storage
     column outright — asking it for the keys and then asking storage again for
     the homes would be two answers to one question. Verified, the default: this
     is a caller resolving records, not a caller reporting on what the last build
     believed, and md-record-index.ts reserves the unverified read for the
     second. */
  function compose(ctx?: any): { rows: any[]; findings: any[]; domains: any[] } {
    /* The ten are built before a single record is read, so an account holding
       nothing still has ten answers rather than none. */
    const outline = new Map<string, any>();
    for (const d of (g.MD_DOMAINS || [])) {
      outline.set(d.id, { id: d.id, label: d.label, option: DOMAIN + d.id,
        pursuits: 0, projects: 0, records: 0, counts: {} as Record<string, number>, at: 0 });
    }
    const domains: any[] = [...outline.values()];
    if (!g.mdRecordIndexRead || !g.mdRecordResolve) return { rows: [], findings: [], domains };
    const homes = (ctx && ctx.homes) || (g.mdRecordHomes ? g.mdRecordHomes() : undefined);

    /* Where every document on the device stands, built at most once and only if
       a row actually asks — it walks all of storage, which is a price worth
       paying once per render and never worth paying per record. */
    let states: Record<string, string> | null = null;
    const syncOf = (keys: string[]): string => {
      if (!keys.length) return BUILT_IN;
      if (!g.mdSyncStateOf) return '';
      if (!states) states = (g.mdSyncStates ? g.mdSyncStates() : {});
      let worst = SYNC_WORDS.length - 1;
      for (const key of keys) {
        const state = g.mdSyncStateOf(key, states);
        const rank = SYNC_WORDS.findIndex((pair) => pair[0] === state);
        if (rank >= 0 && rank < worst) worst = rank;
      }
      return SYNC_WORDS[worst]![1];
    };

    /* Whatever edges the user has earned, keyed by the record they name, so a
       filled cell is overlaid on the row it belongs to in one pass. */
    const cells = new Map<string, Record<string, unknown>>();
    const times = new Map<string, number>();
    for (const entry of (g.mdMembershipList ? g.mdMembershipList(ID) : [])) {
      cells.set(entry.k, entry.v);
      times.set(entry.k, entry.at);
    }

    /* Database names, read at most once and only if a database row is actually
       in the catalogue. mdDatabaseList composes no rows, so calling it from
       inside the row composer cannot recurse. */
    let names: Map<string, string> | null = null;
    const dbName = (id: string): string => {
      if (!names) {
        names = new Map();
        for (const db of (g.mdDatabaseList ? g.mdDatabaseList() : [])) names.set(db.id, db.name);
      }
      return names.get(id) || '';
    };

    /* One pass over the index, gathering what a row needs and what it is
       missing. The domain-level questions cannot be answered yet — "is anything
       filed here" is only knowable once every record has been seen — so they
       wait for the second pass over this same list rather than for a second
       walk of the account. */
    const items: any[] = [];
    const touched = new Map<string, number>();   // the last write inside each project
    for (const [key, home] of Object.entries(g.mdRecordIndexRead())) {
      const resolved = g.mdRecordResolve(key, homes);
      if (!resolved) continue;
      /* A record inside its thirty-day buffer is not a record this catalogue
         holds (0.3). The resolver answers for it so a CARD can draw a countdown
         where it used to go blank; a shelf count that included it would tell
         somebody their account holds things they deleted. */
      if (resolved.trashed) continue;
      const type = resolved.ref.type;
      /* Every document this record has. The home names its identity — null for
         a built-in, whose identity is code — and the writing is a second
         document for the three kinds that keep one. */
      const docs: string[] = [];
      if (typeof home === 'string' && home) docs.push(home);
      const writing = writingOf(resolved);
      if (writing) docs.push(writing.key);
      /* The record's own clock, which the resolver already took the later of
         where a record has two documents. The edge is the fallback and not the
         answer: a record with an edge was FILED at that moment, which is not the
         moment it was written. Zero rather than a guess when there is neither. */
      const at = resolved.at || times.get(key) || 0;
      /* A domain id the app no longer ships is no domain at all. Reading it
         through would file the record under a group with no name, and counting
         it would credit a part of life that does not exist. */
      const domainId = outline.has(resolved.domainId) ? resolved.domainId : null;
      const notes: Array<{ kind: string; note: string }> = [];
      items.push({ key, home, resolved, type, at, writing, docs, domainId, notes });

      const entry = domainId ? outline.get(domainId) : null;
      if (entry) {
        entry.at = Math.max(entry.at, at);
        /* A domain is the shelf, not a thing on it — its own row is why the
           group is never dropped, and counting it would report one record in
           every part of life nothing is filed in. */
        if (type !== 'domain') {
          entry.records += 1;
          entry.counts[type] = (entry.counts[type] || 0) + 1;
        }
      }
      if (resolved.projectId) {
        touched.set(resolved.projectId, Math.max(touched.get(resolved.projectId) || 0, at));
      }

      /* What is missing about this record, asked of the shelf it stands on.
         SPINE already means "records whose place is not a project": a project
         is asked for its pursuit instead, and the rest are asked for neither. */
      if (type === 'project') {
        const project = g.mdProjectById ? g.mdProjectById(resolved.ref.id) : null;
        if (project && !project.pursuitId) notes.push({ kind: 'orphan', note: WORDS.pursuit });
      } else if (!SPINE.has(type) && !resolved.projectId) {
        notes.push({ kind: 'orphan', note: WORDS.project });
      }
      /* A manifestation, a database and a database row carry no domain BY
         DESIGN (md-resolve.ts), so asking them for one would be the catalogue
         reporting its own architecture as the user's mess. */
      if (!domainId && !SPINE_PLACELESS.has(type)) notes.push({ kind: 'unplaced', note: WORDS.unplaced });
      /* Only writing can be missing a link, and a title is not writing — which
         is exactly what bodyOf takes back off the front. A record nobody has
         typed into is left with nothing here, so it raises no finding; flagging
         it would mark every record on the account and bury the ones that
         matter. */
      const written = bodyOf(resolved).trim();
      if (written && g.mdWikiTokens && !g.mdWikiTokens(written).length) {
        notes.push({ kind: 'unlinked', note: WORDS.unlinked });
      }
    }

    /* The second pass: the two findings that are about a stretch of time rather
       than about a single record. A clock of zero is not old — it is the app's
       own seed data, which was never written and so cannot have rotted. */
    const cutoff = Date.now() - STALE_DAYS * 86400000;
    for (const item of items) {
      if (item.type === 'domain') {
        const entry = item.domainId ? outline.get(item.domainId) : null;
        if (!entry) continue;
        if (!entry.records) item.notes.push({ kind: 'empty', note: WORDS.empty });
        else if (entry.at > 0 && entry.at < cutoff) item.notes.push({ kind: 'stale', note: WORDS.stale(dayWords(entry.at)) });
      } else if (item.type === 'project') {
        /* A project is as recent as the latest thing filed under it, not as
           recent as the day somebody last renamed it. */
        const at = Math.max(item.at, touched.get(item.resolved.ref.id) || 0);
        if (at > 0 && at < cutoff) item.notes.push({ kind: 'stale', note: WORDS.stale(dayWords(at)) });
      }
    }

    const rows: any[] = [];
    const findings: any[] = [];
    for (const item of items) {
      const { key, home, resolved, at, writing, docs, notes } = item;
      /* One order for both slices, and it is severity rather than the order the
         passes happened to run in: a person reads the first note in the cell. */
      notes.sort((a: any, b: any) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
      for (const note of notes) {
        findings.push({ ref: key, title: resolved.title, kind: note.kind, note: note.note });
      }
      const above = structure(resolved, dbName);
      rows.push({
        id: key,
        values: { ...(cells.get(key) || {}), [TITLE]: resolved.title, [TYPE]: resolved.type,
          [PARENT]: above.parent, [PROJECT]: above.project,
          /* Storage names the document a person could go and look at, so a
             built-in that has been written in names its writing rather than
             reading blank — blank is for a record that is nowhere on disk. */
          [HOME]: (typeof home === 'string' && home) ? home : (writing ? writing.key : ''),
          [WRITTEN]: stamp(at), [SYNC]: syncOf(docs),
          [DOMAIN]: item.domainId ? DOMAIN + item.domainId : NO_DOMAIN_OPTION,
          [FINDINGS]: notes.map((note: any) => note.note).join(' · ') },
        body: bodyOf(resolved),
        created: at, updated: at,
        member: key,
      });
    }
    /* Named for what a person asks about a part of life — how many things am I
       pursuing there — with the raw tally beside them for anything that wants
       to ask about a kind this file has not thought of. */
    for (const entry of domains) {
      entry.pursuits = entry.counts.pursuit || 0;
      entry.projects = entry.counts.project || 0;
    }
    return { rows, findings, domains };
  }

  /** The three slices, each the whole composition read one way. The table wants
      rows; the AI Organizer wants the queue; a summary wants the outline — and
      none of them can see an account the others cannot. */
  /* ── composed once per change, not once per render ────────────────────────
     compose() reads every record on the account. On a real 5,000-record
     account that is about a second, and opening the Main DB called it four
     times: four seconds of composition to draw sixty-two virtualised rows.
     The table was never the problem; recomposing from storage on every render
     was.

     The cache is retired by mdStorageRevision(), the counter at the storage
     chokepoint that moves on every write and every removal. That is the whole
     reason this is safe to do without listing what the catalogue depends on:
     a day record, a project, a membership edge, a database row and a sync pull
     are all writes, so any of them retires this. A cache that tried to know
     which keys mattered would be wrong about the one it forgot.

     A caller-supplied ctx is NOT cached across, only alongside: answering from
     a composition built against different pre-read homes would be a
     correctness bug, so a different ctx recomposes.

     Callers get a fresh array each time. The rows inside it are shared — the
     same bargain mdDatabaseReadCanonical already makes — but nobody can push,
     splice or sort the cache out from under the next reader. */
  let memo: { revision: number; ctx: any; value: { rows: any[]; findings: any[]; domains: any[] } } | null = null;
  function composed(ctx?: any): { rows: any[]; findings: any[]; domains: any[] } {
    if (typeof g.mdStorageRevision !== 'function') return compose(ctx);   // no signal, no cache
    if (memo && memo.ctx === ctx && memo.revision === g.mdStorageRevision()) return memo.value;
    const value = compose(ctx);
    /* Stamped with the revision AFTER composing, because composing can itself
       write: mdRecordIndexRead rebuilds the index when it is missing or stale.
       Stamping beforehand would leave the memo permanently one revision behind
       its own side effect, and every call would rebuild — which is the bug this
       cache exists to fix, reintroduced by the cache. */
    memo = { revision: g.mdStorageRevision(), ctx, value };
    return value;
  }
  function mdLibrarianRows(ctx?: any): any[] { return composed(ctx).rows.slice(); }
  function mdLibrarianFindings(ctx?: any): any[] { return composed(ctx).findings.slice(); }
  function mdLibrarianDomains(ctx?: any): any[] { return composed(ctx).domains.slice(); }

  /* ── filing a record from the catalogue ──────────────────────────────────── */

  /** Records whose place in the spine is not a project. A project belongs to a
      pursuit, a pursuit to a domain, a database row to its database — offering
      to file any of them under a project would be the one-upward-pointer law
      read backwards, and the Parent column already shows them the truth. */
  const SPINE = new Set(['project', 'pursuit', 'domain', 'craft', 'database', 'dbrow']);

  /** The project a typed value names: an id, which is what an agent holds, or a
      name, which is what a person types. `null` means clear. `undefined` means
      no such project — refused by the caller rather than quietly creating one,
      because a typo should not become a project. */
  function projectFor(value: unknown): string | null | undefined {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return null;
    const byId = g.mdProjectById ? g.mdProjectById(text) : null;
    if (byId) return byId.id;
    const wanted = text.toLowerCase();
    const hit = (g.mdProjectIndex ? g.mdProjectIndex() : [])
      .find((p: any) => typeof p.name === 'string' && p.name.toLowerCase() === wanted);
    return hit ? hit.id : undefined;
  }

  /* A day record's home is its day document, and the write goes through
     mdStoreWriteAll rather than mdStoreWrite: only the all-days door stamps a
     record for the sync merge, and an unstamped edit is one an idle device can
     win a conflict against. The whole map is handed back because that door
     deletes any day it is not told about.

     THE DOMAIN IS REWRITTEN IN THE SAME BREATH. A project owns its domain, so
     filing a record moves its colour too — the same pair the compose sheet
     already writes when a record is filed there. */
  function dayWrite(resolved: any, pid: string | null): boolean {
    for (const kind of ['events', 'records']) {
      const map = g.mdStoreAll ? g.mdStoreAll(kind) : null;
      const list = map && map[resolved.day];
      const rec = Array.isArray(list) ? list.find((r: any) => r && r.uid === resolved.ref.id) : null;
      if (!rec) continue;
      const own = (typeof rec.domain === 'string' && rec.domain) ? rec.domain : null;
      if (pid) rec.pid = pid; else delete rec.pid;
      const domain = g.mdProjectDomainFor ? g.mdProjectDomainFor(pid, own) : own;
      if (domain) rec.domain = domain; else delete rec.domain;
      return !!g.mdStoreWriteAll && g.mdStoreWriteAll(kind, map);
    }
    return false;
  }

  /** What mdLibrarianCellSet would have to be handed to put this cell back where
      it is now — the setter's own vocabulary, not the table's. The Project cell
      READS a project's name, because a name is what a person recognises, but it
      is WRITTEN by anything that names a project; handing back the id keeps a
      restore working across a rename, and `''` is the word this setter already
      understands as "file it nowhere".

      `null` means there is nothing here to put back: a property this module does
      not own, or a record it refuses to file at all. Callers use that to skip
      the undo step rather than to stack one that would fail on the way out. */
  function mdLibrarianCellRead(refKey: unknown, propertyId: unknown): string | null {
    if (propertyId !== PROJECT) return null;
    const resolved = g.mdRecordResolve ? g.mdRecordResolve(refKey) : null;
    if (!resolved || SPINE.has(resolved.ref.type)) return null;
    return (typeof resolved.projectId === 'string' && resolved.projectId) ? resolved.projectId : '';
  }

  /** Write a record-owned cell from the Main DB — the one door through the
      two-kinds-of-cell line. Every branch writes the RECORD'S OWN HOME through
      the door that home already has: a day document, the project-record index,
      the board's scope. Nothing here touches an edge, which is why filing a
      record from the catalogue still costs zero storage documents.

      `changed: false` with `ok: true` is a record already filed where it was
      asked to go — a no-op the caller must not turn into a write or a refusal.

      Locked records never reach here: md-database.ts refuses them one step
      earlier, on the same `locked` grounds every other surface uses. */
  function mdLibrarianCellSet(refKey: unknown, propertyId: unknown, value: unknown): { ok: boolean; changed: boolean } {
    const no = { ok: false, changed: false };
    if (propertyId !== PROJECT) return no;      // title, type and parent are read-only here
    const resolved = g.mdRecordResolve ? g.mdRecordResolve(refKey) : null;
    if (!resolved || SPINE.has(resolved.ref.type)) return no;
    const wanted = projectFor(value);
    if (wanted === undefined) return no;
    if ((resolved.projectId || null) === wanted) return { ok: true, changed: false };
    if (resolved.ref.type === 'board') {
      const scope = wanted && g.mdRecordRefKey ? g.mdRecordRefKey({ type: 'project', id: wanted }) : '';
      return (g.mdBoardSetScope && g.mdBoardSetScope(resolved.ref.id, scope)) ? { ok: true, changed: true } : no;
    }
    if (resolved.day) return dayWrite(resolved, wanted) ? { ok: true, changed: true } : no;
    return (g.mdProjectRecordSetProject && g.mdProjectRecordSetProject(resolved.ref.id, wanted))
      ? { ok: true, changed: true } : no;
  }

  Object.assign(g, {
    MD_LIBRARIAN_ID: ID,
    mdLibrarianDatabase, mdLibrarianOwns, mdLibrarianRows, mdLibrarianCellSet,
    mdLibrarianCellRead, mdLibrarianFindings, mdLibrarianDomains,
  });

  // CommonJS escape hatch, following md-record.ts and md-record-index.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_LIBRARIAN_ID: ID, mdLibrarianDatabase, mdLibrarianOwns,
      mdLibrarianRows, mdLibrarianCellSet, mdLibrarianCellRead, mdLibrarianFindings,
      mdLibrarianDomains };
  }
})(typeof window !== 'undefined' ? window : globalThis);
